/**
 * ⚠️ FORKED COMPONENT — DO NOT MERGE BACK ⚠️
 *
 * FORKED FROM: src/components/search/SearchFilterBar.tsx
 *
 * LR DIRECT VERSION
 * This file is intentionally forked to keep LR Direct fully isolated from the main Search Loads system.
 *
 * RULES:
 * - Do NOT import or reuse logic from Search Loads
 * - Do NOT connect to `loads` table
 * - Only use `direct_loads`, `direct_load_stops`, `direct_bids`
 * - Changes here must NOT affect Search Loads
 *
 * If you need shared behavior, duplicate — do not abstract.
 */
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { Search, Loader2, RotateCcw, ArrowLeftRight, X, CalendarIcon, ChevronDown, ChevronUp } from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { LocationAutocomplete } from "@/components/ui/location-autocomplete";
import { EquipmentSelect } from "@/components/ui/equipment-select";
import { EQUIPMENT_FLAT_LIST } from "@/lib/equipmentTypes";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";

import type { SearchTabFilters } from "@/components/search/ActiveSearchTabs";

export interface DirectFilterParams {
  origin: string;
  destination: string;
  equipmentTypes: string[];
  loadType: "full" | "partial" | "other" | "both";
  loadNature: "all" | "spot" | "dedicated" | "contract";
  minLength: string;
  minWeight: string;
  pickupDate: string | null;
}

interface DirectSearchFilterBarProps {
  onSearch: (params: DirectFilterParams) => void;
  isSearching?: boolean;
  resultCount?: number;
  filterValues?: SearchTabFilters;
  onFilterChange?: (filters: SearchTabFilters) => void;
}

/* ─── tiny field wrapper ──────────────────────────────────────────── */
function FilterCell({
  label,
  children,
  className,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col gap-0 px-2 py-0.5", className)}>
      <span className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      {children}
    </div>
  );
}

function Divider() {
  return <div className="w-px bg-border/80 self-stretch shrink-0 my-1" />;
}

function getEquipShortLabel(enumVal: string): string {
  const found = EQUIPMENT_FLAT_LIST.find((t) => t.enumValue === enumVal);
  return found ? found.name : enumVal;
}

/* ─── Active Filter Chips ────────────────────────────────────────── */
function ActiveFilterChips({
  origin, destination, equipmentTypes, loadType, loadNature, minLength, minWeight, pickupDate, onRemove,
}: {
  origin: string; destination: string; equipmentTypes: string[];
  loadType: string; loadNature: string; minLength: string; minWeight: string; pickupDate: Date | null;
  onRemove: (key: string, value?: string) => void;
}) {
  const chips: { key: string; label: string; value?: string }[] = [];
  if (origin.trim()) chips.push({ key: "origin", label: `Origin: ${origin}` });
  if (destination.trim()) chips.push({ key: "destination", label: `Dest: ${destination}` });
  equipmentTypes.forEach((v) => chips.push({ key: "equipment", label: getEquipShortLabel(v), value: v }));
  if (loadType && loadType !== "both") chips.push({ key: "loadType", label: `Size: ${loadType}` });
  if (loadNature && loadNature !== "all") chips.push({ key: "loadNature", label: `Nature: ${loadNature}` });
  if (minLength.trim()) chips.push({ key: "minLength", label: `Min Length: ${minLength} ft` });
  if (minWeight.trim()) chips.push({ key: "minWeight", label: `Min Weight: ${minWeight} lbs` });
  if (pickupDate) chips.push({ key: "pickupDate", label: `Pickup: ${format(pickupDate, "MMM d")}` });
  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5 px-1">
      {chips.map((chip, i) => (
        <span
          key={`${chip.key}-${chip.value ?? i}`}
          className="h-6 px-2 text-xs rounded-sm bg-muted inline-flex items-center gap-1 text-foreground"
        >
          {chip.label}
          <X
            className="h-3 w-3 cursor-pointer text-muted-foreground hover:text-destructive"
            onClick={() => onRemove(chip.key, chip.value)}
          />
        </span>
      ))}
    </div>
  );
}

export function DirectSearchFilterBar({
  onSearch,
  isSearching,
  resultCount,
  filterValues,
  onFilterChange,
}: DirectSearchFilterBarProps) {
  const [origin, setOrigin] = useState(filterValues?.origin ?? "");
  const [destination, setDestination] = useState(filterValues?.destination ?? "");
  const [equipmentTypes, setEquipmentTypes] = useState<string[]>(filterValues?.equipmentTypes ?? []);
  const [loadType, setLoadType] = useState<"full" | "partial" | "other" | "both">(filterValues?.loadType ?? "both");
  const [loadNature, setLoadNature] = useState<"all" | "spot" | "dedicated" | "contract">(filterValues?.loadNature ?? "all");
  const [minLength, setMinLength] = useState(filterValues?.minLength ?? "");
  const [minWeight, setMinWeight] = useState(filterValues?.minWeight ?? "");
  const [pickupDate, setPickupDate] = useState<Date | null>(
    filterValues?.pickupDate ? new Date(filterValues.pickupDate + "T00:00:00") : null
  );

  const onFilterChangeRef = useRef(onFilterChange);
  onFilterChangeRef.current = onFilterChange;

  useEffect(() => {
    onFilterChangeRef.current?.({
      origin,
      destination,
      dhoRadius: "150",
      dhdRadius: "150",
      equipmentTypes,
      loadType,
      loadNature,
      minLength,
      minWeight,
      pickupDate: pickupDate ? format(pickupDate, "yyyy-MM-dd") : null,
      hideCanada: false,
      searchBack: "7",
      sortBy: filterValues?.sortBy ?? "age_newest",
    });
  }, [origin, destination, equipmentTypes, loadType, loadNature, minLength, minWeight, pickupDate]);

  // Collapse by default on all screen sizes; user can expand to access filters.
  const [expanded, setExpanded] = useState(false);

  const activeChipsCount = useMemo(() => {
    let n = 0;
    if (origin.trim()) n++;
    if (destination.trim()) n++;
    n += equipmentTypes.length;
    if (loadType && loadType !== "both") n++;
    if (loadNature && loadNature !== "all") n++;
    if (minLength.trim()) n++;
    if (minWeight.trim()) n++;
    if (pickupDate) n++;
    return n;
  }, [origin, destination, equipmentTypes, loadType, loadNature, minLength, minWeight, pickupDate]);

  const handleSearch = () => {
    onSearch({ origin, destination, equipmentTypes, loadType, loadNature, minLength, minWeight, pickupDate: pickupDate ? format(pickupDate, "yyyy-MM-dd") : null });
  };

  const handleClear = () => {
    setOrigin("");
    setDestination("");
    setEquipmentTypes([]);
    setLoadType("both");
    setLoadNature("all");
    setMinLength("");
    setMinWeight("");
    setPickupDate(null);
  };

  const handleSwapLane = () => {
    const prevOrigin = origin;
    setOrigin(destination);
    setDestination(prevOrigin);
  };

  const inputBase =
    "h-5 text-[11px] border-0 bg-transparent p-0 shadow-none focus-visible:ring-0";

  return (
    <div className="flex flex-col gap-1 w-full bg-background pb-1">
      {/* ═══ COLLAPSED TRIGGER — always visible when the filter bar is collapsed ═══ */}
      {!expanded && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="flex items-center gap-2 rounded-lg border border-border bg-card shadow-sm px-3 py-2 text-left hover:bg-muted/40 transition-colors"
        >
          <Search className="h-4 w-4 text-muted-foreground shrink-0" />
          <span className="flex-1 min-w-0 text-sm font-medium text-foreground">
            Search & filter loads
          </span>
          {activeChipsCount > 0 && (
            <span className="text-xs text-muted-foreground">
              {activeChipsCount} filter{activeChipsCount !== 1 ? "s" : ""} applied
            </span>
          )}
          <ChevronDown className="h-4 w-4 text-muted-foreground shrink-0" />
        </button>
      )}

      <Collapsible open={expanded}>
        <CollapsibleContent className="transition-all duration-300 data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
          <div className="flex flex-col gap-1">
            {/* ═══ ROW 1 — Primary Filters ═══ */}
            <div className="flex flex-wrap items-end gap-0 rounded-lg border border-border bg-card shadow-sm">
              {/* Origin */}
              <FilterCell label="Origin" className="min-w-[120px] flex-1 sm:flex-[0.64] basis-[45%] sm:basis-auto">
                <LocationAutocomplete
                  value={origin}
                  onChange={(v) => setOrigin(v)}
                  placeholder="City, ST"
                  className={cn(inputBase, "truncate")}
                />
              </FilterCell>

              <Divider />

              {/* Swap Lane */}
              <div className="self-center shrink-0 px-0.5">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={handleSwapLane}
                  className="h-5 w-5 text-muted-foreground hover:text-foreground"
                  title="Swap origin and destination"
                >
                  <ArrowLeftRight className="h-3.5 w-3.5" />
                </Button>
              </div>

              {/* Destination */}
              <FilterCell label="Destination" className="min-w-[120px] flex-1 sm:flex-[0.64] basis-[45%] sm:basis-auto">
                <LocationAutocomplete
                  value={destination}
                  onChange={(v) => setDestination(v)}
                  placeholder="City, ST"
                  className={cn(inputBase, "truncate")}
                />
              </FilterCell>

              <Divider />

              {/* Equipment */}
              <FilterCell label="Equipment" className="w-full sm:w-[252px] sm:max-w-[288px]">
                <EquipmentSelect
                  value={equipmentTypes}
                  onChange={(v) => setEquipmentTypes(Array.isArray(v) ? v : [v])}
                  mode="multi"
                  className={inputBase}
                />
              </FilterCell>

              <Divider />

              {/* Size */}
              <FilterCell label="Size" className="w-[110px]">
                <Select value={loadType} onValueChange={(v) => setLoadType(v as "full" | "partial" | "other" | "both")}>
                  <SelectTrigger className={cn(inputBase, "w-full")}>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="both">All sizes</SelectItem>
                    <SelectItem value="full">Full</SelectItem>
                    <SelectItem value="partial">Partial</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </FilterCell>

              <Divider />

              {/* Load Nature */}
              <FilterCell label="Nature" className="w-[110px]">
                <Select value={loadNature} onValueChange={(v) => setLoadNature(v as "all" | "spot" | "dedicated" | "contract")}>
                  <SelectTrigger className={cn(inputBase, "w-full")}>
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    <SelectItem value="spot">Spot</SelectItem>
                    <SelectItem value="dedicated">Dedicated</SelectItem>
                    <SelectItem value="contract">Contract</SelectItem>
                  </SelectContent>
                </Select>
              </FilterCell>

              <Divider />

              {/* Length */}
              <FilterCell label="Length (ft)" className="w-[96px]">
                <Input
                  type="number"
                  placeholder="Min"
                  value={minLength}
                  onChange={(e) => setMinLength(e.target.value)}
                  className={inputBase}
                />
              </FilterCell>

              <Divider />

              {/* Weight */}
              <FilterCell label="Weight (lbs)" className="w-[104px]">
                <Input
                  type="number"
                  placeholder="Min"
                  value={minWeight}
                  onChange={(e) => setMinWeight(e.target.value)}
                  className={inputBase}
                />
              </FilterCell>

              <Divider />

              {/* Pickup Date */}
              <FilterCell label="Pickup Date" className="w-[138px]">
                <Popover>
                  <PopoverTrigger asChild>
                    <button
                      className={cn(
                        inputBase,
                        "w-full flex items-center gap-1 text-left cursor-pointer",
                        !pickupDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="h-3 w-3 shrink-0" />
                      {pickupDate ? format(pickupDate, "MMM d, yyyy") : "Any date"}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={pickupDate ?? undefined}
                      onSelect={(d) => setPickupDate(d ?? null)}
                      initialFocus
                      className={cn("p-3 pointer-events-auto")}
                    />
                  </PopoverContent>
                </Popover>
              </FilterCell>

              <Divider />

              {/* Search Button — full width on mobile, inline on tablet+ */}
              <div className="px-2 py-1 flex items-center w-full sm:w-auto">
                <Button
                  onClick={handleSearch}
                  disabled={isSearching}
                  className="rounded-lg bg-brand-teal text-white font-semibold px-6 hover:bg-brand-teal/90 shrink-0 w-full sm:w-auto h-11 sm:h-9"
                >
                  {isSearching ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Search className="h-4 w-4 mr-2" />
                  )}
                  Search Direct Loads
                </Button>
              </div>
            </div>

            {/* ═══ ROW 2 — Secondary Controls ═══ */}
            <div className="flex flex-wrap items-center gap-3 px-1 text-[11px]">
              {resultCount !== undefined && (
                <span className="text-muted-foreground">
                  {resultCount} result{resultCount !== 1 ? "s" : ""}
                </span>
              )}
              <div className="flex-1" />
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground gap-1"
              >
                <RotateCcw className="h-3 w-3" />
                Clear
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setExpanded(false)}
                className="h-6 px-2 text-[11px] text-muted-foreground hover:text-foreground gap-1"
              >
                <ChevronUp className="h-3 w-3" />
                Hide filters
              </Button>
            </div>

            {/* ═══ ROW 3 — Active Filter Chips ═══ */}
            <ActiveFilterChips
              origin={origin}
              destination={destination}
              equipmentTypes={equipmentTypes}
              loadType={loadType}
              loadNature={loadNature}
              minLength={minLength}
              minWeight={minWeight}
              pickupDate={pickupDate}
              onRemove={(key, value) => {
                switch (key) {
                  case "origin": setOrigin(""); break;
                  case "destination": setDestination(""); break;
                  case "equipment": setEquipmentTypes(equipmentTypes.filter(v => v !== value)); break;
                  case "loadType": setLoadType("both"); break;
                  case "loadNature": setLoadNature("all"); break;
                  case "minLength": setMinLength(""); break;
                  case "minWeight": setMinWeight(""); break;
                  case "pickupDate": setPickupDate(null); break;
                }
              }}
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
